import React from 'react';
import dayjs from 'dayjs';
import { ShiftDetailsTableProps } from '../../types';
import { formatHourlyRateDisplay } from 'utils/formatHourlyRateDisplay';
import { getDentistShiftRoleLongLabel } from 'utils/dentist/shiftRoleLabel';
import { 
  DetailRow, 
  IconWrapper, 
  DetailContent, 
  DetailLabel, 
  DetailValue, 
  DownTimeNote, 
  PaidText 
} from '../../styles';

const ShiftDetailsTable: React.FC<ShiftDetailsTableProps> = ({ shifts, hasInvoice, shiftType, hourlyRate }) => {
  if (!shifts || shifts.length === 0) {
    return <div>No shift details available</div>;
  }

  // For completed jobs, we typically have one shift
  const shift = shifts[0];
  const professionalType = getDentistShiftRoleLongLabel(shiftType);
  const formattedHourlyRate = formatHourlyRateDisplay(hourlyRate);

  return (
    <>
      <DetailRow>
        <IconWrapper>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 2C8.89543 2 8 2.89543 8 4C8 5.10457 8.89543 6 10 6C11.1046 6 12 5.10457 12 4C12 2.89543 11.1046 2 10 2Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 8V14M10 14L7 18M10 14L13 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconWrapper>
        <DetailContent>
          <DetailLabel>Shift Type</DetailLabel>
          <DetailValue>{professionalType}</DetailValue>
        </DetailContent>
      </DetailRow>

      <DetailRow>
        <IconWrapper>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="3" y="5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M7 3V7M13 3V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <rect x="7" y="10" width="2" height="2" fill="currentColor"/>
            <rect x="11" y="10" width="2" height="2" fill="currentColor"/>
          </svg>
        </IconWrapper>
        <DetailContent>
          <DetailLabel>Shift Date</DetailLabel>
          <DetailValue>{dayjs(shift.shiftDate).format('MMM D, YYYY')}</DetailValue>
        </DetailContent>
      </DetailRow>

      <DetailRow>
        <IconWrapper>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 6V10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </IconWrapper>
        <DetailContent>
          <DetailLabel>Posted Shift Hours</DetailLabel>
          <DetailValue>
            {hasInvoice && shift.actualStart && shift.actualEnd
              ? `${shift.actualStart} - ${shift.actualEnd}`
              : `${shift.postedStart} - ${shift.postedEnd}`}
          </DetailValue>
        </DetailContent>
      </DetailRow>

      {formattedHourlyRate && (
        <DetailRow data-testid="completed-shift-hourly-rate-row">
          <IconWrapper>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 6V14M12.5 7.5C12.5 6.67 11.6 6 10.5 6H9.5C8.4 6 7.5 6.67 7.5 7.5C7.5 8.33 8.4 9 9.5 9H10.5C11.6 9 12.5 9.67 12.5 10.5C12.5 11.33 11.6 12 10.5 12H9.5C8.4 12 7.5 11.33 7.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </IconWrapper>
          <DetailContent>
            <DetailLabel>Hourly rate</DetailLabel>
            <DetailValue data-testid="completed-shift-hourly-rate">{formattedHourlyRate}</DetailValue>
          </DetailContent>
        </DetailRow>
      )}

      <DownTimeNote>
        Down Time is: <PaidText $isUnpaid={shift.gapUnpaid === 1}>
          {shift.gapUnpaid === 1 ? 'Unpaid' : 'Paid'}
        </PaidText>
      </DownTimeNote>
    </>
  );
};

export default ShiftDetailsTable;
